import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SidebarModule } from 'primeng/sidebar';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import {
  InAppNotificationDto,
  InAppNotificationService,
} from '../../../utilities/services/in-app-notification/in-app-notification.service';

type NotificationFilter = 'all' | 'unread' | 'read';
type NotificationCategory = 'all_category' | 'oers' | 'membership' | 'payment';

interface NotificationOption<T extends string> {
  value: T;
  labelKey: string;
}

interface NotificationItem {
  listKey: string;
  id: number;
  title: string;
  message: string;
  timeAgo: string;
  category: Exclude<NotificationCategory, 'all_category'>;
  status: 'read' | 'unread';
  accent: 'blue' | 'purple' | 'orange' | 'pink';
  showViewMore?: boolean;
  actionUrl?: string | null;
  details?: {
    heading: string;
    greeting: string;
    body: string[];
    signature: string[];
    attachmentName?: string;
  };
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(diffSec) < 60) {
    return rtf.format(-diffSec, 'second');
  }
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) {
    return rtf.format(-diffMin, 'minute');
  }
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) {
    return rtf.format(-diffHr, 'hour');
  }
  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 30) {
    return rtf.format(-diffDay, 'day');
  }
  const diffMonth = Math.round(diffDay / 30);
  if (Math.abs(diffMonth) < 12) {
    return rtf.format(-diffMonth, 'month');
  }
  return rtf.format(-Math.round(diffDay / 365), 'year');
}

function mapModuleToCategory(module: string | null | undefined): Exclude<NotificationCategory, 'all_category'> {
  if (!module) {
    return 'oers';
  }
  const m = module.toLowerCase();
  if (m.includes('membership')) {
    return 'membership';
  }
  if (m.includes('payment') || m.includes('invoice')) {
    return 'payment';
  }
  return 'oers';
}

function mapPriorityToAccent(priority: string | undefined): NotificationItem['accent'] {
  switch ((priority ?? 'normal').toLowerCase()) {
    case 'high':
    case 'urgent':
      return 'pink';
    case 'low':
      return 'purple';
    default:
      return 'blue';
  }
}

function dedupeNotifications(items: InAppNotificationDto[]): InAppNotificationDto[] {
  const seen = new Set<string>();
  const out: InAppNotificationDto[] = [];
  for (const item of items) {
    const key = `${item.notification_id ?? item.id}_${item.created_at}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(item);
  }
  return out;
}

function mapDtoToItem(dto: InAppNotificationDto, index: number): NotificationItem {
  const listKey = `${dto.notification_id ?? dto.id}_${dto.created_at}_${index}`;
  const body = dto.body?.trim() ?? '';
  const message = body;
  const longBody = body.length > 160;
  const showViewMore = longBody && body.length > 0;

  const details = showViewMore
    ? {
        heading: dto.title,
        greeting: '',
        body: body ? [body] : [],
        signature: [] as string[],
        attachmentName: undefined as string | undefined,
      }
    : undefined;

  return {
    listKey,
    id: dto.id,
    title: dto.title,
    message,
    timeAgo: formatRelativeTime(dto.created_at),
    category: mapModuleToCategory(dto.module),
    status: dto.is_read ? 'read' : 'unread',
    accent: mapPriorityToAccent(dto.priority),
    showViewMore,
    actionUrl: dto.action_url,
    details,
  };
}

@Component({
  selector: 'app-notification-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SidebarModule, SkeletonLoaderComponent],
  templateUrl: './notification-manager.component.html',
  styleUrl: './notification-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationManagerComponent implements OnInit, OnDestroy {
  private readonly inAppNotifications = inject(InAppNotificationService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchReload$ = new Subject<void>();

  selectedFilter: NotificationFilter = 'all';
  selectedCategory: NotificationCategory = 'all_category';
  searchTerm = '';
  expandedNotificationId: string | null = null;
  sidebarVisible = false;
  optionsMenuOpen = false;
  /** Latches this component's own scroll lock (not a shared overlay counter). */
  private pageScrollLocked = false;
  private lockedScrollY = 0;
  markAllReadLoading = false;

  private isInsideNotificationScrollArea(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.notification-list');
  }

  /** Blocks background touch scroll (Safari/iOS); allows the notification list. */
  private readonly preventBackgroundTouch = (event: TouchEvent): void => {
    if (this.isInsideNotificationScrollArea(event.target)) {
      return;
    }
    event.preventDefault();
  };

  /** Blocks background trackpad/mouse wheel scroll; allows the notification list. */
  private readonly preventBackgroundWheel = (event: WheelEvent): void => {
    if (this.isInsideNotificationScrollArea(event.target)) {
      return;
    }
    event.preventDefault();
  };

  notifications: NotificationItem[] = [];
  filteredNotifications: NotificationItem[] = [];
  notificationsLoading = false;
  loadingMore = false;
  serverUnreadCount = 0;
  private readonly pageSize = 20;
  private currentPage = 1;
  private totalCount = 0;
  hasMore = false;
  private loadRequestId = 0;

  readonly filters: NotificationOption<NotificationFilter>[] = [
    { value: 'all', labelKey: 'all' },
    { value: 'unread', labelKey: 'unread' },
    { value: 'read', labelKey: 'read' },
  ];
  readonly categories: NotificationOption<NotificationCategory>[] = [
    { value: 'all_category', labelKey: 'all_category' },
    { value: 'oers', labelKey: 'Registration' },
    { value: 'membership', labelKey: 'Membership' },
    { value: 'payment', labelKey: 'payment' },
  ];

  get unreadCount(): number {
    return this.serverUnreadCount;
  }

  get displayUnreadCount(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  private isReadQueryParam(): boolean | undefined {
    if (this.selectedFilter === 'read') {
      return true;
    }
    if (this.selectedFilter === 'unread') {
      return false;
    }
    return undefined;
  }

  private moduleQueryParam(): string | undefined {
    return this.selectedCategory === 'all_category' ? undefined : this.selectedCategory;
  }

  private refreshFilteredNotifications(): void {
    this.filteredNotifications = this.notifications;
  }

  private updateHasMore(): void {
    this.hasMore = this.notifications.length < this.totalCount;
  }

  loadNotifications(reset = true): void {
    if (!reset) {
      if (this.notificationsLoading || this.loadingMore || !this.hasMore) {
        return;
      }
      this.loadingMore = true;
    } else {
      this.loadRequestId += 1;
      this.currentPage = 1;
      this.hasMore = true;
      this.notificationsLoading = true;
      this.loadingMore = false;
    }

    const requestId = this.loadRequestId;
    const pageToLoad = reset ? 1 : this.currentPage + 1;
    const search = this.searchTerm.trim() || undefined;

    this.inAppNotifications
      .getInAppNotifications(
        pageToLoad,
        this.pageSize,
        this.isReadQueryParam(),
        search,
        this.moduleQueryParam(),
      )
      .pipe(
        finalize(() => {
          if (requestId !== this.loadRequestId) {
            return;
          }
          this.notificationsLoading = false;
          this.loadingMore = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (page) => {
          if (requestId !== this.loadRequestId) {
            return;
          }
          this.serverUnreadCount = page.unread_count;
          this.totalCount = page.total;
          this.currentPage = page.page;
          const unique = dedupeNotifications(page.items);
          const mapped = unique.map((dto, i) =>
            mapDtoToItem(dto, reset ? i : this.notifications.length + i),
          );

          if (reset) {
            this.notifications = mapped;
          } else {
            const existingIds = new Set(this.notifications.map((item) => item.id));
            this.notifications = [
              ...this.notifications,
              ...mapped.filter((item) => !existingIds.has(item.id)),
            ];
          }

          this.updateHasMore();
          this.refreshFilteredNotifications();
          this.cdr.markForCheck();
        },
        error: () => {
          if (requestId !== this.loadRequestId) {
            return;
          }
          if (reset) {
            this.notifications = [];
            this.filteredNotifications = [];
            this.serverUnreadCount = 0;
            this.totalCount = 0;
            this.hasMore = false;
          }
          this.cdr.markForCheck();
        },
      });
  }

  onNotificationListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el || this.notificationsLoading || this.loadingMore || !this.hasMore) {
      return;
    }
    const thresholdPx = 80;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx) {
      this.loadNotifications(false);
    }
  }

  ngOnInit(): void {
    this.searchReload$
      .pipe(
        debounceTime(300),
        map(() => this.searchTerm.trim()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadNotifications());

    this.loadNotifications();
  }

  onSearchTermChange(): void {
    this.searchReload$.next();
  }

  setFilter(filter: NotificationFilter): void {
    this.selectedFilter = filter;
    this.loadNotifications();
  }

  setCategory(category: NotificationCategory): void {
    this.selectedCategory = category;
    this.loadNotifications(true);
  }

  get hasUnreadNotifications(): boolean {
    return this.notifications.some((item) => item.status === 'unread');
  }

  openSidebar(): void {
    this.setSidebarVisible(true);
  }

  closeSidebar(): void {
    this.setSidebarVisible(false);
  }

  onSidebarVisibleChange(visible: boolean): void {
    this.setSidebarVisible(visible);
  }

  private setSidebarVisible(visible: boolean): void {
    if (visible === this.sidebarVisible) {
      return;
    }
    this.sidebarVisible = visible;
    if (visible) {
      this.lockPageScroll();
      this.loadNotifications();
      return;
    }
    this.optionsMenuOpen = false;
    this.unlockPageScroll();
  }

  private getWindowScrollY(): number {
    return (
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  private lockPageScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined' || this.pageScrollLocked) {
      return;
    }
    this.pageScrollLocked = true;
    this.lockedScrollY = this.getWindowScrollY();

    // Class drives overflow lock in styles.scss (Chrome/Firefox/Edge/desktop).
    document.documentElement.classList.add('notification-overlay-open');
    document.body.classList.add('notification-overlay-open');

    // Safari/iOS (and some mobile browsers) ignore overflow:hidden — pin the body.
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.maxWidth = '100%';

    // Capture phase + non-passive so Safari cannot scroll the page behind the panel.
    document.addEventListener('touchmove', this.preventBackgroundTouch, {
      passive: false,
      capture: true,
    });
    document.addEventListener('wheel', this.preventBackgroundWheel, {
      passive: false,
      capture: true,
    });
  }

  private unlockPageScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined' || !this.pageScrollLocked) {
      return;
    }
    this.pageScrollLocked = false;

    document.removeEventListener('touchmove', this.preventBackgroundTouch, true);
    document.removeEventListener('wheel', this.preventBackgroundWheel, true);

    // Do not remove PrimeNG's p-overflow-hidden — other overlays may still own it.
    document.documentElement.classList.remove('notification-overlay-open');
    document.body.classList.remove('notification-overlay-open');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.maxWidth = '';

    const restoreY = this.lockedScrollY;
    this.lockedScrollY = 0;
    // Safari applies scroll restore more reliably after style cleanup paints.
    requestAnimationFrame(() => {
      window.scrollTo(0, restoreY);
      document.documentElement.scrollTop = restoreY;
      document.body.scrollTop = restoreY;
    });
  }

  toggleOptionsMenu(event: Event): void {
    event.stopPropagation();
    this.optionsMenuOpen = !this.optionsMenuOpen;
  }

  markAllAsRead(): void {
    if (this.markAllReadLoading) {
      return;
    }
    const unreadIds = this.notifications
      .filter((item) => item.status === 'unread')
      .map((item) => item.id);
    if (!unreadIds.length) {
      this.optionsMenuOpen = false;
      return;
    }
    this.markAllReadLoading = true;
    this.inAppNotifications
      .markInAppNotificationsRead(unreadIds)
      .pipe(
        finalize(() => {
          this.markAllReadLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.optionsMenuOpen = false;
          this.notifications = this.notifications.map((item) => ({
            ...item,
            status: 'read',
          }));
          this.refreshFilteredNotifications();
          this.serverUnreadCount = 0;
          this.loadNotifications();
        },
        error: () => {
          this.optionsMenuOpen = false;
          this.cdr.markForCheck();
        },
      });
  }

  toggleNotificationDetails(notification: NotificationItem): void {
    const key = notification.listKey;
    const wasExpanded = this.expandedNotificationId === key;
    this.expandedNotificationId = wasExpanded ? null : key;
    if (!wasExpanded) {
      this.markNotificationRead(notification);
    }
  }

  isExpanded(notificationId: string): boolean {
    return this.expandedNotificationId === notificationId;
  }

  openNotification(notification: NotificationItem, event: Event): void {
    event.stopPropagation();
    if (!notification.actionUrl) {
      return;
    }
    this.markNotificationRead(notification);
    const url = notification.actionUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      void this.router.navigateByUrl(url);
      this.closeSidebar();
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private markNotificationRead(notification: NotificationItem): void {
    if (notification.status !== 'unread') {
      return;
    }
    this.inAppNotifications
      .markInAppNotificationsRead([notification.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          notification.status = 'read';
          this.serverUnreadCount = Math.max(0, this.serverUnreadCount - 1);
          this.refreshFilteredNotifications();
          this.cdr.markForCheck();
        },
        error: () => {
          // Leave unread on failure
        },
      });
  }

  trackByNotification(_: number, notification: NotificationItem): string {
    return notification.listKey;
  }

  trackByOption<T extends string>(_: number, option: NotificationOption<T>): string {
    return option.value;
  }

  trackByValue(_: number, value: string): string {
    return value;
  }

  ngOnDestroy(): void {
    this.searchReload$.complete();
    this.unlockPageScroll();
  }
}
